/**
 * TrialCountdownBanner
 *
 * Shows a sticky banner when the user's free trial ends within 5 days.
 * Three urgency levels: info (≤5 days), warning (≤3 days), critical (≤1 day).
 * Dismissible per-session (state held in React, not persisted).
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { X, Sparkles } from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'

export function TrialCountdownBanner() {
  const [dismissed, setDismissed] = useState(false)
  const { subscription, isLoading } = useSubscription()

  if (isLoading || dismissed) return null
  if (subscription?.state !== 'trialing' || !subscription.trialEndsAt) return null

  const trialEnd = new Date(subscription.trialEndsAt)
  const msLeft = trialEnd.getTime() - Date.now()
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24))

  // Show when ≤ 5 days remain and trial hasn't expired
  if (daysLeft > 5 || daysLeft <= 0) return null

  const urgency = daysLeft <= 1 ? 'critical' : daysLeft <= 3 ? 'warning' : 'info'

  const STYLES = {
    critical: {
      banner: 'bg-inauspicious/15 border-inauspicious/30',
      text:   'text-inauspicious',
      icon:   'text-inauspicious',
      cta:    'bg-inauspicious/80 hover:bg-inauspicious text-star-white',
    },
    warning: {
      banner: 'bg-gold/10 border-gold/25',
      text:   'text-gold',
      icon:   'text-gold',
      cta:    'bg-gold/20 hover:bg-gold/30 text-gold border border-gold/40',
    },
    info: {
      banner: 'bg-violet/10 border-violet/20',
      text:   'text-violet-glow',
      icon:   'text-violet-glow',
      cta:    'bg-gradient-to-r from-violet to-violet-mid text-star-white hover:shadow-glow-violet',
    },
  }

  const s = STYLES[urgency]
  const dayLabel = daysLeft === 1 ? 'today' : `in ${daysLeft} days`

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative z-20 flex items-center gap-3 px-4 py-2.5 border-b ${s.banner} text-sm transition-all`}
    >
      <Sparkles className={`w-3.5 h-3.5 shrink-0 ${s.icon}`} aria-hidden="true" />

      <p className={`flex-1 min-w-0 ${s.text}`}>
        <span className="font-medium">
          {daysLeft <= 1 ? 'Your free trial expires today.' : `Your free trial ends ${dayLabel}.`}
        </span>
        {' '}Add a payment method to keep your Premium access.
      </p>

      <Link
        to="/billing"
        className={`shrink-0 px-3 py-1 rounded-lg text-xs font-medium transition-all ${s.cta}`}
      >
        {daysLeft <= 1 ? 'Upgrade now' : 'Subscribe →'}
      </Link>

      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss trial expiry notice"
        className="shrink-0 p-1 rounded-md text-star-dim hover:text-star-soft hover:bg-white/5 transition-all"
      >
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}
